import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { 
  ClipboardList, 
  Save, 
  Share2, 
  Trash2, 
  Check, 
  Copy, 
  QrCode,
  Shuffle,
  X,
  Send,
  Clock,
  Beaker,
  Wine,
  Zap,
  Users,
  BookOpen,
  Leaf,
  GlassWater,
  ChevronsUpDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

const TEST_TYPES = [
  { 
    id: "full", 
    name: "Full Spec Test", 
    description: "Ingredients (brand & measurements), garnish, and glassware",
    icon: ClipboardList,
    color: "text-[hsl(var(--primary))]"
  },
  { 
    id: "ingredients", 
    name: "Ingredients Only", 
    description: "Focus on ingredient brands and measurements",
    icon: Beaker,
    color: "text-blue-400"
  },
  { 
    id: "presentation", 
    name: "Presentation Test", 
    description: "Garnish and glassware only - the visual elements",
    icon: Wine,
    color: "text-purple-400"
  },
  {
    id: "garnish",
    name: "Garnish Only",
    description: "Test knowledge of garnishes for each cocktail",
    icon: Leaf,
    color: "text-green-400"
  },
  {
    id: "glassware",
    name: "Glassware Only",
    description: "Test knowledge of which glass to use",
    icon: GlassWater,
    color: "text-orange-400"
  },
  { 
    id: "speed", 
    name: "Speed Round", 
    description: "30 seconds per cocktail - beat the clock!",
    icon: Zap,
    color: "text-[#C36A2F]"
  }
];

export default function TestCreationPage() {
  const [cocktails, setCocktails] = useState([]);
  const [filters, setFilters] = useState(null);
  const [staff, setStaff] = useState([]);
  const [savedTests, setSavedTests] = useState([]);
  const [selectedCocktails, setSelectedCocktails] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [filterCategory, setFilterCategory] = useState("");
  const [filterSpirits, setFilterSpirits] = useState([]); // Changed to array for multi-select
  const [filterIngredients, setFilterIngredients] = useState([]); // Filter by specific ingredient (e.g. "Lime Juice")
  const [ingredientPickerOpen, setIngredientPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Test type
  const [testType, setTestType] = useState("full");
  
  // Save test dialog
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [testName, setTestName] = useState("");
  const [testDescription, setTestDescription] = useState("");
  const [testCategory, setTestCategory] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Assign test dialog
  const [assignTest, setAssignTest] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [assignmentResults, setAssignmentResults] = useState(null);
  const [quickAssigningId, setQuickAssigningId] = useState(null);
  
  // Share dialog (for viewing existing assignments)
  const [shareTest, setShareTest] = useState(null);
  const [testAssignments, setTestAssignments] = useState([]);
  const [copied, setCopied] = useState(null);
  
  // Practice link dialog
  const [practiceTest, setPracticeTest] = useState(null);
  const [practiceLink, setPracticeLink] = useState(null);
  const [creatingPracticeLink, setCreatingPracticeLink] = useState(false);

  useEffect(() => {
    Promise.all([fetchCocktails(), fetchFilters(), fetchSavedTests(), fetchStaff()]).finally(() => setLoading(false));
  }, []);

  const fetchCocktails = async () => {
    try {
      const response = await axios.get(`${API}/cocktails`);
      setCocktails(response.data);
    } catch (error) {
      console.error("Failed to fetch cocktails:", error);
    }
  };

  const fetchFilters = async () => {
    try {
      const response = await axios.get(`${API}/cocktails-meta/filters`);
      setFilters(response.data);
    } catch (error) {
      console.error("Failed to fetch filters:", error);
    }
  };

  const fetchSavedTests = async () => {
    try {
      const response = await axios.get(`${API}/saved-tests`);
      setSavedTests(response.data);
    } catch (error) {
      console.error("Failed to fetch saved tests:", error);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff`);
      setStaff(response.data);
    } catch (error) {
      console.error("Failed to fetch staff:", error);
    }
  };

  // Clear selected cocktails when filters change (prevents stale selections from previous filter)
  useEffect(() => {
    setSelectedCocktails([]);
  }, [filterCategory, filterSpirits, filterIngredients]);

  const filteredCocktails = cocktails.filter(c => {
    if (filterCategory && filterCategory !== "all" && c.category !== filterCategory) return false;
    if (filterSpirits.length > 0 && !filterSpirits.includes(c.base_spirit)) return false;
    if (filterIngredients.length > 0 && !([...(c.ingredients || []), ...(c.batch_breakdown || [])]).some(ing => filterIngredients.includes(ing.name))) return false;
    if (searchQuery && !c.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const toggleCocktail = (id) => {
    setSelectedCocktails(prev => 
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelectedCocktails(filteredCocktails.map(c => c.id));
  };

  const clearSelection = () => {
    setSelectedCocktails([]);
  };

  const selectRandom = (count) => {
    const shuffled = [...filteredCocktails].sort(() => Math.random() - 0.5);
    setSelectedCocktails(shuffled.slice(0, count).map(c => c.id));
  };

  const handleSaveTest = async () => {
    if (!testName.trim()) {
      toast.error("Please enter a test name");
      return;
    }
    if (selectedCocktails.length === 0) {
      toast.error("Please select at least one cocktail");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/saved-tests`, {
        name: testName.trim(),
        description: testDescription.trim() || null,
        category: testCategory.trim() || null,
        test_type: testType,
        time_limit: testType === "speed" ? 30 : null,
        cocktail_ids: selectedCocktails
      });
      setSavedTests(prev => [...prev, response.data]);
      setShowSaveDialog(false);
      setTestName("");
      setTestDescription("");
      setTestCategory("");
      toast.success("Test saved successfully");
    } catch (error) {
      toast.error("Failed to save test");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTest = async (testId) => {
    try {
      await axios.delete(`${API}/saved-tests/${testId}`);
      setSavedTests(prev => prev.filter(t => t.id !== testId));
      toast.success("Test deleted");
    } catch (error) {
      toast.error("Failed to delete test");
    }
  };

  const handleLoadTest = (test) => {
    setSelectedCocktails(test.cocktail_ids);
    setTestType(test.test_type || "full");
    toast.success(`Loaded ${test.cocktail_ids.length} cocktails`);
  };

  const openAssignDialog = (test) => {
    setAssignTest(test);
    setSelectedStaff([]);
    setAssignmentResults(null);
  };

  const toggleStaffSelection = (staffId) => {
    setSelectedStaff(prev => 
      prev.includes(staffId) ? prev.filter(s => s !== staffId) : [...prev, staffId]
    );
  };

  const handleAssignTest = async () => {
    if (selectedStaff.length === 0) {
      toast.error("Please select at least one staff member");
      return;
    }

    setAssigning(true);
    try {
      const response = await axios.post(`${API}/test-assignments`, {
        test_id: assignTest.id,
        staff_ids: selectedStaff
      });
      setAssignmentResults(response.data.assignments);
      toast.success(`Test assigned to ${response.data.assignments.length} staff members`);
    } catch (error) {
      toast.error("Failed to assign test");
    } finally {
      setAssigning(false);
    }
  };

  const quickAssignToTeam = async (test) => {
    if (staff.length === 0) {
      toast.error("No team members to assign to yet — add staff first");
      return;
    }
    setQuickAssigningId(test.id);
    try {
      const response = await axios.post(`${API}/test-assignments`, {
        test_id: test.id,
        staff_ids: staff.map((m) => m.id),
      });
      const count = response.data.assignments.length;
      toast.success(`"${test.name}" assigned to ${count} teammate${count !== 1 ? 's' : ''}`);
      // Open the share dialog so the admin can grab links instantly
      setShareTest(test);
      try {
        const r = await axios.get(`${API}/test-assignments?test_id=${test.id}`);
        setTestAssignments(r.data);
      } catch {
        setTestAssignments([]);
      }
    } catch (error) {
      toast.error("Failed to assign to team");
    } finally {
      setQuickAssigningId(null);
    }
  };

  const openShareDialog = async (test) => {
    setShareTest(test);
    try {
      const response = await axios.get(`${API}/test-assignments?test_id=${test.id}`);
      setTestAssignments(response.data);
    } catch (error) {
      setTestAssignments([]);
    }
  };

  const copyLink = (code) => {
    const url = `${FRONTEND_URL}/take-test/${code}`;
    navigator.clipboard.writeText(url);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
    toast.success("Link copied!");
  };

  const copyPracticeLink = (code) => {
    const url = `${FRONTEND_URL}/practice/${code}`;
    navigator.clipboard.writeText(url);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
    toast.success("Practice link copied!");
  };

  const generateQRCode = (code) => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`${FRONTEND_URL}/take-test/${code}`)}`;
  };

  const generatePracticeQRCode = (code) => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`${FRONTEND_URL}/practice/${code}`)}`;
  };

  const openPracticeDialog = async (test) => {
    setPracticeTest(test);
    setPracticeLink(null);
  };

  const createPracticeLink = async () => {
    setCreatingPracticeLink(true);
    try {
      const response = await axios.post(`${API}/practice-links?test_id=${practiceTest.id}`);
      setPracticeLink(response.data);
      toast.success("Practice link created!");
    } catch (error) {
      toast.error("Failed to create practice link");
    } finally {
      setCreatingPracticeLink(false);
    }
  };

  const getTestTypeInfo = (typeId) => TEST_TYPES.find(t => t.id === typeId) || TEST_TYPES[0];

  return (
    <div data-testid="test-creation-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Create Test</h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">
            Select cocktails, choose test type, and assign to staff
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowSaveDialog(true)}
            disabled={selectedCocktails.length === 0}
            data-testid="save-test-btn"
          >
            <Save size={18} className="mr-2" />
            Save Test ({selectedCocktails.length})
          </Button>
        </div>
      </div>

      {/* Test Type Selection */}
      <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Test Type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {TEST_TYPES.map((type) => {
              const Icon = type.icon;
              return (
                <div
                  key={type.id}
                  onClick={() => setTestType(type.id)}
                  className={`p-4 rounded-lg cursor-pointer transition-all ${
                    testType === type.id
                      ? 'bg-[hsl(var(--primary))]/10 border-2 border-[hsl(var(--primary))]'
                      : 'bg-[hsl(var(--secondary))] border-2 border-transparent hover:border-[hsl(var(--border))]'
                  }`}
                  data-testid={`test-type-${type.id}`}
                >
                  <Icon className={`${type.color} mb-2`} size={24} />
                  <p className="font-medium">{type.name}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{type.description}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Saved Tests */}
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Saved Tests</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 max-h-[500px] overflow-y-auto">
            {savedTests.length === 0 ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-4" data-testid="no-saved-tests">
                No saved tests yet
              </p>
            ) : (
              savedTests.map((test) => {
                const typeInfo = getTestTypeInfo(test.test_type);
                const TypeIcon = typeInfo.icon;
                return (
                  <div
                    key={test.id}
                    className="p-3 rounded-lg bg-[hsl(var(--secondary))] space-y-2"
                    data-testid={`saved-test-${test.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <TypeIcon size={16} className={typeInfo.color} />
                        <div>
                          <p className="font-medium">{test.name}</p>
                          <p className="text-xs text-[hsl(var(--muted-foreground))]">
                            {test.cocktail_ids.length} cocktails • {typeInfo.name}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleLoadTest(test)}
                        className="flex-1"
                        data-testid={`load-test-${test.id}`}
                      >
                        Load
                      </Button>
                      <Button
                        size="sm"
                        className="bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
                        onClick={() => openAssignDialog(test)}
                        data-testid={`assign-test-${test.id}`}
                      >
                        <Send size={14} className="mr-1" /> Assign
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => quickAssignToTeam(test)}
                        disabled={quickAssigningId === test.id || staff.length === 0}
                        title={staff.length === 0 ? "Add staff members first" : `Instantly assign to all ${staff.length} teammate(s)`}
                        className="border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
                        data-testid={`quick-assign-team-${test.id}`}
                      >
                        <Users size={14} className="mr-1" />
                        {quickAssigningId === test.id ? "Assigning..." : `Team (${staff.length})`}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openPracticeDialog(test)}
                        className="text-blue-400 border-blue-400/30 hover:bg-blue-400/10"
                        data-testid={`practice-test-${test.id}`}
                      >
                        <BookOpen size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openShareDialog(test)}
                        data-testid={`share-test-${test.id}`}
                      >
                        <Share2 size={14} />
                      </Button>
                      <Link to={`/study/${test.id}`} target="_blank">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-400 border-green-400/30 hover:bg-green-400/10"
                          data-testid={`flashcards-test-${test.id}`}
                        >
                          <Wine size={14} className="mr-1" /> Study
                        </Button>
                      </Link>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteTest(test.id)}
                        className="text-red-500 hover:text-red-400"
                        data-testid={`delete-test-${test.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Cocktail Selection */}
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardList className="text-[hsl(var(--primary))]" size={20} />
              Select Cocktails
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] w-40"
                data-testid="cocktail-search"
              />
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-36" data-testid="filter-category-select">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {filters?.categories?.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(filterCategory || filterSpirits.length > 0 || filterIngredients.length > 0 || searchQuery) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setFilterCategory(""); setFilterSpirits([]); setFilterIngredients([]); setSearchQuery(""); }}
                  data-testid="clear-filters"
                >
                  <X size={14} className="mr-1" /> Clear
                </Button>
              )}
            </div>
            
            {/* Spirit Multi-Select */}
            <div className="space-y-2">
              <Label className="text-sm text-[hsl(var(--muted-foreground))]">
                Filter by Spirit {filterSpirits.length > 0 && `(${filterSpirits.length} selected)`}
              </Label>
              <div className="flex flex-wrap gap-2">
                {filters?.base_spirits?.map((spirit) => (
                  <div
                    key={spirit}
                    onClick={() => {
                      setFilterSpirits(prev => 
                        prev.includes(spirit) 
                          ? prev.filter(s => s !== spirit)
                          : [...prev, spirit]
                      );
                    }}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full cursor-pointer transition-all text-sm ${
                      filterSpirits.includes(spirit)
                        ? 'bg-[hsl(var(--primary))] text-black'
                        : 'bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))]'
                    }`}
                    data-testid={`spirit-filter-${spirit}`}
                  >
                    {spirit}
                  </div>
                ))}
              </div>
            </div>

            {/* Ingredient Multi-Select — e.g. find every cocktail that contains "Lime Juice", not just by base spirit */}
            <div className="space-y-2">
              <Label className="text-sm text-[hsl(var(--muted-foreground))]">
                Filter by Ingredient {filterIngredients.length > 0 && `(${filterIngredients.length} selected)`}
              </Label>
              <Popover open={ingredientPickerOpen} onOpenChange={setIngredientPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={ingredientPickerOpen}
                    className="w-full sm:w-72 justify-between bg-[hsl(var(--secondary))] border-[hsl(var(--border))] font-normal"
                    data-testid="ingredient-filter-trigger"
                  >
                    <span className="truncate text-left">
                      {filterIngredients.length > 0 ? `${filterIngredients.length} ingredient${filterIngredients.length > 1 ? 's' : ''} selected` : "Search ingredients (e.g. Lime Juice)..."}
                    </span>
                    <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0 bg-[hsl(var(--popover))] border-[hsl(var(--border))]" align="start">
                  <Command>
                    <CommandInput placeholder="Type an ingredient..." data-testid="ingredient-filter-search" />
                    <CommandList>
                      <CommandEmpty>No ingredient found.</CommandEmpty>
                      <CommandGroup>
                        {filters?.ingredient_names?.map((name) => (
                          <CommandItem
                            key={name}
                            value={name}
                            onSelect={() => {
                              setFilterIngredients(prev =>
                                prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
                              );
                            }}
                            data-testid={`ingredient-filter-option-${name}`}
                          >
                            <Check size={14} className={`mr-2 ${filterIngredients.includes(name) ? 'opacity-100' : 'opacity-0'}`} />
                            {name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {filterIngredients.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {filterIngredients.map((name) => (
                    <div
                      key={name}
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-[hsl(var(--primary))] text-black text-sm"
                      data-testid={`ingredient-filter-pill-${name}`}
                    >
                      {name}
                      <button
                        onClick={() => setFilterIngredients(prev => prev.filter(n => n !== name))}
                        aria-label={`Remove ${name} filter`}
                        data-testid={`ingredient-filter-remove-${name}`}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={selectAll} data-testid="select-all-btn">
                Select All ({filteredCocktails.length})
              </Button>
              <Button size="sm" variant="outline" onClick={() => selectRandom(5)} data-testid="random-5-btn">
                <Shuffle size={14} className="mr-1" /> Random 5
              </Button>
              <Button size="sm" variant="outline" onClick={() => selectRandom(10)} data-testid="random-10-btn">
                <Shuffle size={14} className="mr-1" /> Random 10
              </Button>
              {selectedCocktails.length > 0 && (
                <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="clear-selection-btn">
                  Clear Selection
                </Button>
              )}
            </div>

            {/* Cocktail List */}
            {loading ? (
              <div className="py-8 text-center text-[hsl(var(--muted-foreground))]" data-testid="loading-cocktails">
                Loading...
              </div>
            ) : filteredCocktails.length === 0 ? (
              <div className="py-8 text-center text-[hsl(var(--muted-foreground))]" data-testid="no-cocktails-found">
                No cocktails found
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto pr-2" data-testid="cocktail-selection-list">
                {filteredCocktails.map((cocktail) => (
                  <div
                    key={cocktail.id}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedCocktails.includes(cocktail.id)
                        ? 'bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30'
                        : 'bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--accent))]'
                    }`}
                    onClick={() => toggleCocktail(cocktail.id)}
                    data-testid={`cocktail-select-${cocktail.id}`}
                  >
                    <Checkbox
                      checked={selectedCocktails.includes(cocktail.id)}
                      onCheckedChange={() => toggleCocktail(cocktail.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{cocktail.name}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                        {cocktail.base_spirit} • {cocktail.category}
                        {filterIngredients.length > 0 && (() => {
                          const matched = [...(cocktail.ingredients || []), ...(cocktail.batch_breakdown || [])]
                            .map(ing => ing.name)
                            .filter(n => filterIngredients.includes(n));
                          return matched.length > 0 ? ` • contains ${[...new Set(matched)].join(', ')}` : '';
                        })()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Save Test Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="save-test-dialog">
          <DialogHeader>
            <DialogTitle>Save Test Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Test Name *</Label>
              <Input
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
                placeholder="e.g., Weekly Gin Test"
                className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                data-testid="test-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={testDescription}
                onChange={(e) => setTestDescription(e.target.value)}
                placeholder="Optional description..."
                className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                data-testid="test-description-input"
              />
            </div>
            <div className="p-3 rounded-lg bg-[hsl(var(--secondary))]">
              <p className="text-sm">
                <span className="text-[hsl(var(--muted-foreground))]">Test Type:</span>{" "}
                <span className="font-medium">{getTestTypeInfo(testType).name}</span>
              </p>
              <p className="text-sm">
                <span className="text-[hsl(var(--muted-foreground))]">Cocktails:</span>{" "}
                <span className="font-medium">{selectedCocktails.length} selected</span>
              </p>
            </div>
            <Button
              onClick={handleSaveTest}
              disabled={saving}
              className="w-full btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
              data-testid="confirm-save-test-btn"
            >
              {saving ? "Saving..." : "Save Test"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Test Dialog */}
      <Dialog open={!!assignTest} onOpenChange={() => { setAssignTest(null); setAssignmentResults(null); }}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md" data-testid="assign-test-dialog">
          <DialogHeader>
            <DialogTitle>Assign Test: {assignTest?.name}</DialogTitle>
          </DialogHeader>
          
          {assignmentResults ? (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                <p className="text-green-400 font-medium mb-2">Test Assigned Successfully!</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  Share these links with your staff members:
                </p>
              </div>
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {assignmentResults.map((assignment) => (
                  <div key={assignment.id} className="p-3 rounded-lg bg-[hsl(var(--secondary))]">
                    <p className="font-medium mb-2">{assignment.staff_name}</p>
                    <div className="flex gap-2">
                      <Input
                        readOnly
                        value={`${FRONTEND_URL}/take-test/${assignment.access_code}`}
                        className="bg-[hsl(var(--card))] border-[hsl(var(--border))] text-xs"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyLink(assignment.access_code)}
                      >
                        {copied === assignment.access_code ? <Check size={14} /> : <Copy size={14} />}
                      </Button>
                    </div>
                    <div className="mt-2 flex justify-center">
                      <img src={generateQRCode(assignment.access_code)} alt="QR" className="w-24 h-24 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Select staff members to assign this test. Each person will receive a unique link.
              </p>

              {/* 1-Click Assign-to-Team Quick Action */}
              {staff.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedStaff(staff.map((m) => m.id))}
                    className="border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
                    data-testid="assign-all-team-btn"
                  >
                    <Users size={14} className="mr-1.5" />
                    Assign to entire team ({staff.length})
                  </Button>
                  {selectedStaff.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedStaff([])}
                      data-testid="clear-staff-selection-btn"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              )}

              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {staff.map((member) => (
                  <div
                    key={member.id}
                    onClick={() => toggleStaffSelection(member.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedStaff.includes(member.id)
                        ? 'bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30'
                        : 'bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--accent))]'
                    }`}
                    data-testid={`assign-staff-${member.id}`}
                  >
                    <Checkbox checked={selectedStaff.includes(member.id)} />
                    <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary))]/20 flex items-center justify-center">
                      <span className="text-[hsl(var(--primary))] text-sm font-semibold">
                        {member.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="font-medium">{member.name}</span>
                  </div>
                ))}
              </div>
              <Button
                onClick={handleAssignTest}
                disabled={assigning || selectedStaff.length === 0}
                className="w-full btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
                data-testid="confirm-assign-btn"
              >
                <Send size={16} className="mr-2" />
                {assigning ? "Assigning..." : `Assign to ${selectedStaff.length} Staff`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Assignments Dialog */}
      <Dialog open={!!shareTest} onOpenChange={() => setShareTest(null)}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg" data-testid="view-assignments-dialog">
          <DialogHeader>
            <DialogTitle>Test Assignments: {shareTest?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {testAssignments.length === 0 ? (
              <div className="text-center py-6">
                <Users size={40} className="mx-auto text-[hsl(var(--muted-foreground))] mb-3" />
                <p className="text-[hsl(var(--muted-foreground))]">No assignments yet</p>
                <Button
                  className="mt-4"
                  onClick={() => { setShareTest(null); openAssignDialog(shareTest); }}
                >
                  Assign to Staff
                </Button>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {testAssignments.map((assignment) => (
                  <div key={assignment.id} className="p-3 rounded-lg bg-[hsl(var(--secondary))]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{assignment.staff_name}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        assignment.status === 'completed' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-[#C36A2F]/20 text-[#C36A2F]'
                      }`}>
                        {assignment.status === 'completed' ? 'Completed' : 'Pending'}
                      </span>
                    </div>
                    {assignment.status !== 'completed' && (
                      <>
                        <div className="flex gap-2 mb-2">
                          <Input
                            readOnly
                            value={`${FRONTEND_URL}/take-test/${assignment.access_code}`}
                            className="bg-[hsl(var(--card))] border-[hsl(var(--border))] text-xs"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyLink(assignment.access_code)}
                          >
                            {copied === assignment.access_code ? <Check size={14} /> : <Copy size={14} />}
                          </Button>
                        </div>
                        <div className="flex justify-center">
                          <img src={generateQRCode(assignment.access_code)} alt="QR" className="w-20 h-20 rounded" />
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Practice Link Dialog */}
      <Dialog open={!!practiceTest} onOpenChange={() => { setPracticeTest(null); setPracticeLink(null); }}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md" data-testid="practice-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="text-blue-400" size={20} />
              Practice Mode: {practiceTest?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-blue-400 font-medium mb-2">Practice Mode Features:</p>
              <ul className="text-sm text-[hsl(var(--muted-foreground))] space-y-1">
                <li>• Immediate feedback after each question</li>
                <li>• Results not recorded (no impact on scores)</li>
                <li>• Unlimited retries</li>
                <li>• Anyone with the link can practice</li>
              </ul>
            </div>

            {practiceLink ? (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <p className="text-green-400 font-medium mb-2">Practice Link Created!</p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    Share this link with your staff:
                  </p>
                </div>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={`${FRONTEND_URL}/practice/${practiceLink.practice_code}`}
                      className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] text-xs"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyPracticeLink(practiceLink.practice_code)}
                    >
                      {copied === practiceLink.practice_code ? <Check size={14} /> : <Copy size={14} />}
                    </Button>
                  </div>
                  <div className="flex justify-center">
                    <img 
                      src={generatePracticeQRCode(practiceLink.practice_code)} 
                      alt="Practice QR Code" 
                      className="w-32 h-32 rounded" 
                    />
                  </div>
                </div>
              </div>
            ) : (
              <Button
                onClick={createPracticeLink}
                disabled={creatingPracticeLink}
                className="w-full bg-blue-500 text-white hover:bg-blue-600"
                data-testid="create-practice-link-btn"
              >
                <BookOpen size={16} className="mr-2" />
                {creatingPracticeLink ? "Creating..." : "Generate Practice Link"}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
